import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { LoginForm } from "@/components/login-form";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { useAuth } from "@/contexts/AuthContext";
import { useEnterprise } from "@/contexts/EnterpriseContext";
import { enterpriseLogoUrl } from "@/services/platformEnterprisesService";

export function EnterpriseLoginPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading } = useAuth();
  const { enterprise, loading: enterpriseLoading } = useEnterprise();
  const { t } = useTranslation();
  const [logoFailed, setLogoFailed] = useState(false);

  const returnTo = searchParams.get("returnTo") ?? "/";

  useEffect(() => {
    if (enterprise?.name) {
      document.title = `${enterprise.name} | Gestionalo`;
    }
  }, [enterprise?.name]);

  if (isLoading || enterpriseLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-primary">
        <LoadingLogo size="lg" className="text-primary-foreground" />
      </div>
    );
  }

  if (isAuthenticated) return <Navigate to={returnTo} replace />;

  const logoSrc =
    enterprise?.id && !logoFailed
      ? enterpriseLogoUrl(enterprise.id)
      : undefined;

  return (
    <div className="h-svh overflow-y-auto bg-primary">
      <div className="relative flex min-h-full flex-col items-center justify-center p-6 md:p-10">
        <div className="absolute right-4 top-4 md:right-6 md:top-6">
          <LanguageSwitcher />
        </div>
        <div className="w-full max-w-sm md:max-w-4xl">
          <LoginForm
            returnTo={returnTo}
            branding={{
              logoUrl: logoSrc,
              logoAlt: enterprise?.name ?? "Enterprise",
              title: enterprise?.name
                ? t("auth.login.titleEnterprise", {
                    name: enterprise.name,
                    defaultValue: `Iniciá sesión en ${enterprise.name}`,
                  })
                : undefined,
              hideGoogleSso: true,
              hideRegister: true,
            }}
          />
          {enterprise?.id && !logoFailed && (
            <img
              src={enterpriseLogoUrl(enterprise.id)}
              alt=""
              className="hidden"
              onError={() => setLogoFailed(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
